import React, { useState, useEffect } from 'react';
import { Search, Heart, ChevronLeft, ChevronRight, ChevronDown, X } from 'lucide-react';
import Footer from '../Nav/Footer';

// Create caches for product details
const detailsCache = new Map();

// Helper function to get product type
const getProductType = (title) => {
  if (title.includes('ข้าว')) return 'ข้าว';
  if (title.includes('แป้ง')) return 'แป้ง';
  if (title.includes('สาคู')) return 'สาคู';
  return 'อื่นๆ';
};

const ProductDetail = ({ isOpen, onClose, product }) => {
  const [details, setDetails] = useState(() => detailsCache.get(product.folder) || '');

  useEffect(() => {
    const fetchDetails = async () => {
      if (!isOpen) return;
      
      if (detailsCache.has(product.folder)) {
        setDetails(detailsCache.get(product.folder));
        return;
      }

      try {
        const response = await fetch(`/RiceProduction/${product.folder}/${product.txtFile}`);
        const text = await response.text();
        detailsCache.set(product.folder, text);
        setDetails(text);
      } catch (error) {
        console.error('Error fetching details:', error);
        const errorMessage = 'ไม่พบข้อมูลรายละเอียดสินค้า';
        detailsCache.set(product.folder, errorMessage);
        setDetails(errorMessage);
      }
    };

    fetchDetails();
  }, [isOpen, product.folder, product.txtFile]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-[#140FA4]">{product.title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <img
              src={`/RiceProduction/${product.folder}/${product.image}`}
              alt={product.title}
              className="w-full max-w-[140px] mx-auto md:w-full  object-contain rounded-lg"
            />
          </div>
          <div className="w-full h-[500px] overflow-y-auto">
            <h3 className="font-semibold mb-2">รายละเอียดสินค้า</h3>
            <p className="text-gray-600 whitespace-pre-line">
              {details || 'กำลังโหลดข้อมูล...'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductCard = ({ title, folder, image, onReadMore }) => {
  const productType = getProductType(title);
  
  return (
    <div className="bg-white rounded-lg p-4 shadow-md flex flex-col h-full transition-transform duration-300 hover:shadow-lg hover:scale-105">
      <div className="bg-gray-50 rounded-lg p-4 flex-grow relative">
        <img
          src={`/RiceProduction/${folder}/${image}`}
          alt={title}
          className="w-full h-48 sm:h-56 object-contain"
        />
      </div>

      <div className="mt-4 flex flex-col items-center">
        <h3 className="text-sm font-medium text-gray-800 text-center">{title}</h3>
        <div className="flex gap-2 mt-2">
          {productType === 'ข้าว' && 
            ['1KG', '5KG', '25KG'].map((format) => (
              <span
                key={format}
                className="px-2 py-1 text-xs border border-indigo-600 text-indigo-600 rounded-lg"
              >
                {format}
              </span>
            ))
          }
          {(productType === 'แป้ง' || productType === 'สาคู') && 
            ['500g', '1KG'].map((format) => (
              <span
                key={format}
                className="px-2 py-1 text-xs border border-indigo-600 text-indigo-600 rounded-lg"
              >
                {format}
              </span>
            ))
          }
        </div>
        <div className="flex gap-4 mt-4 w-full justify-between items-center">
          <button 
            onClick={onReadMore}
            className="text-white bg-[#140FA4] rounded-lg px-4 py-2 text-sm hover:bg-[#0d0c9f] transition"
          >
            รายละเอียด
          </button>
          <button className="bg-[#140FA4] text-white w-10 h-10 flex justify-center items-center rounded-full hover:bg-[#0d0c9f] transition">
            <Heart size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

const OurProducts = () => {
  const [selectedCategory, setSelectedCategory] = useState('ทั้งหมด');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);

  // Define product types
  const productTypes = ['ทั้งหมด', 'ข้าว', 'แป้ง', 'สาคู'];

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetch('/RiceProduction_structure_utf8.json');
        const data = await response.json();
        
        const productList = Object.entries(data.RiceProduction).map(([folder, files]) => {
          // Find the .txt file and .png file
          const txtFile = Object.keys(files).find(file => file.endsWith('.txt'));
          const mainImage = Object.keys(files).find(file => file.match(/^\d+\.png$/));
          
          return {
            id: parseInt(folder.replace('Product', '')),
            folder,
            title: txtFile ? txtFile.replace('.txt', '') : '',
            txtFile,
            image: mainImage
          };
        });

        // Sort by ID
        productList.sort((a, b) => a.id - b.id);
        setProducts(productList);
      } catch (error) {
        console.error('Error loading product structure:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Filter products based on selected category
  const filteredProducts = selectedCategory === 'ทั้งหมด'
    ? products
    : products.filter(p => getProductType(p.title) === selectedCategory);

  const handleReadMore = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#140FA4] mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col mt-12">
      <div className="container mx-auto px-4 py-6 sm:py-10">
        <h1 className="text-3xl sm:text-5xl font-bold text-center text-[#140FA4] mb-6">
          ผลิตภัณฑ์ของเรา
        </h1>

        <div className="flex flex-wrap justify-center gap-4">
          <div className="relative w-40">
            <select
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#140FA4] appearance-none"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {productTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {filteredProducts.map((product) => (
          <ProductCard 
            key={product.id} 
            {...product} 
            onReadMore={() => handleReadMore(product)}
          />
        ))}
      </div>

      {isModalOpen && (
        <ProductDetail
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          product={selectedProduct}
        />
      )}

      <Footer />
    </div>
  );
};

export default OurProducts;